import { useState } from "react";
import { money } from "../money.js";

export default function CartSidebar({
	cart,
	checkoutUrl,
	onApplyCoupon,
	onRemoveCoupon,
}) {
	const totals = cart.totals;
	const coupons = cart.coupons;
	const [couponCode, setCouponCode] = useState("");
	const [couponError, setCouponError] = useState(null);
	const [applying, setApplying] = useState(false);
	const [couponOpen, setCouponOpen] = useState(false);

	const subtotal = parseInt(totals.total_items, 10);
	const discount = parseInt(totals.total_discount, 10);
	const shipping = parseInt(totals.total_shipping, 10);
	const tax = parseInt(totals.total_tax, 10);
	const total = parseInt(totals.total_price, 10);

	// currently-selected shipping (full choice stays on checkout)
	const selectedRate = (cart.shipping_rates || [])
		.flatMap((pkg) => pkg.shipping_rates || [])
		.find((r) => r.selected);

	const handleApply = async (e) => {
		e.preventDefault();
		const code = couponCode.trim();
		if (!code) return;
		setApplying(true);
		setCouponError(null);
		const err = await onApplyCoupon(code);
		if (err) setCouponError(err);
		else setCouponCode("");
		setApplying(false);
	};

	return (
		<div className="zc-sidebar">
			<h3 className="zc-sidebar__title">Cart totals</h3>

			<button
				type="button"
				className="zc-sidebar__coupon-toggle"
				onClick={() => setCouponOpen((o) => !o)}
				aria-expanded={couponOpen}
			>
				Add coupons
				<span aria-hidden="true">{couponOpen ? "–" : "+"}</span>
			</button>

			{couponOpen && (
				<form className="zc-sidebar__promo-form" onSubmit={handleApply}>
					<input
						type="text"
						value={couponCode}
						onChange={(e) => setCouponCode(e.target.value)}
						placeholder="Coupon code"
						disabled={applying}
					/>
					<button type="submit" disabled={applying || !couponCode.trim()}>
						{applying ? "…" : "Apply"}
					</button>
				</form>
			)}
			{couponError && <p className="zc-sidebar__promo-error">{couponError}</p>}

			{coupons?.length > 0 && (
				<div className="zc-sidebar__coupons">
					{coupons.map((c) => (
						<div key={c.code} className="zc-sidebar__coupon-tag">
							<span>{c.code}</span>
							<button onClick={() => onRemoveCoupon(c.code)} aria-label={`Remove coupon ${c.code}`}>
								&times;
							</button>
						</div>
					))}
				</div>
			)}

			<div className="zc-sidebar__totals">
				<div className="zc-sidebar__row">
					<span>Subtotal</span>
					<span>{money(subtotal, totals)}</span>
				</div>
				{discount > 0 && (
					<div className="zc-sidebar__row zc-sidebar__row--discount">
						<span>Discount</span>
						<span>-{money(discount, totals)}</span>
					</div>
				)}
				<div className="zc-sidebar__row">
					<span>{selectedRate ? selectedRate.name : "Shipping"}</span>
					<span>{shipping > 0 ? money(shipping, totals) : "Free"}</span>
				</div>
			</div>

			<div className="zc-sidebar__total">
				<span>Estimated total</span>
				<span className="zc-sidebar__total-value">{money(total, totals)}</span>
			</div>
			{tax > 0 && (
				<p className="zc-sidebar__tax">
					Including {money(tax, totals)} {totals.tax_lines?.[0]?.name || "GST"}
				</p>
			)}

			<a href={checkoutUrl} className="zc-sidebar__checkout">
				Proceed to checkout
			</a>

			<ul className="zc-sidebar__assurances">
				<li>PayNow, card and Atome accepted</li>
				<li>14-day returns on unused gear</li>
				<li>Custom builds strung in-house</li>
			</ul>

			<a href="/shop/" className="zc-sidebar__keep">
				Keep shopping
			</a>
		</div>
	);
}
